import React, { useMemo } from "react"
import classNames from "classnames"
import { sum } from "lodash-es"

import { useTranslation } from "@/locales"
import { parseColorString } from "@/utils/i18n"
import { IPlayerSummary } from "../../pages/Record"
import Copper from "../Copper"
import CopperDraw from "../CopperDraw"
import Count from "../Count"
import Ending from "../Ending"
import PartTitle from "../PartTitle"
import SectionTitle from "../SectionTitle"

import styles from "./index.module.less"

interface IProps {
    data?: IPlayerSummary | null
    className?: string
    style?: React.CSSProperties
}

const PlayerSummary: React.FC<IProps> = (props) => {
    const { data, className, style } = props
    const { t } = useTranslation()

    const clearInfo = useMemo(() => {
        const result: {
            ending: number
            grade: number
        }[] = []

        for (const [key, value] of Object.entries(data?.clearInfo || {})) {
            const endingIndex = parseInt(key)
            result.push({
                ending: endingIndex,
                grade: value,
            })
        }

        return result.sort((a, b) => a.ending - b.ending)
    }, [data?.clearInfo])
    const maxGrade = useMemo(
        () => Math.max(...clearInfo.map((item) => item.grade)),
        [clearInfo]
    )
    const maxGradeLabel = useMemo(() => {
        const difficultyLabel = t("difficulty.normal")
        if (maxGrade > 0) {
            return `${difficultyLabel}-${maxGrade}`
        }
        return difficultyLabel
    }, [maxGrade, t])

    const maxCopperGain = useMemo(() => {
        if (!data) {
            return null
        }

        const { maxCopperGain, copperGain } = data

        if (maxCopperGain?.id && copperGain?.[maxCopperGain.id]) {
            return {
                id: maxCopperGain.id,
                name: maxCopperGain.name,
                value: copperGain[maxCopperGain.id],
            }
        }
        return null
    }, [data])

    const copperDrawTotal = useMemo(() => {
        if (!data) {
            return 0
        }
        return sum(Object.values(data.copperDraw || {}))
    }, [data])

    const fragmentData = useMemo(() => {
        if (!data?.copperDraw) {
            return []
        }

        return [
            {
                type: "GOOD",
                value: data?.copperDraw?.GOOD || 0,
            },
            {
                type: "NORMAL",
                value: data?.copperDraw?.NORMAL || 0,
            },
            {
                type: "BAD",
                value: data?.copperDraw?.BAD || 0,
            },
        ].filter((item) => item.value > 0)
    }, [data?.copperDraw])

    return (
        <div
            className={classNames(
                styles.playerSummary,
                data && styles.show,
                className
            )}
            style={style}
        >
            <PartTitle
                label={t("player.1.title")}
                subLabel={t("player.1.title.sub")}
            />
            {clearInfo.length ? (
                <section className={styles.ending}>
                    <SectionTitle
                        className={styles.title}
                        data={[
                            {
                                text: t("player.clear.1"),
                                color: false,
                            },
                            {
                                text: maxGradeLabel,
                                color: true,
                            },
                            {
                                text: t("player.clear.2"),
                                color: false,
                            },
                            {
                                text: clearInfo
                                    .reduce((acc, { ending, grade }) => {
                                        if (grade === maxGrade) {
                                            acc.push(t(`ending.${ending}`))
                                        }
                                        return acc
                                    }, [] as string[])
                                    .join(t("player.clear.split")),
                                color: true,
                                hideBackground: true,
                            },
                            {
                                text: t("player.clear.3"),
                                color: false,
                            },
                        ]}
                    />
                    <div className={styles.content}>
                        {clearInfo.map((i) => (
                            <Ending key={i.ending} {...i} />
                        ))}
                    </div>
                </section>
            ) : null}
            <section className={styles.gold}>
                <SectionTitle data={parseColorString(t("player.gold"))} />
                <div className={styles.content}>
                    <div className={styles.detail}>
                        <Count value={(data?.gold || 0).toLocaleString()} />
                    </div>
                </div>
            </section>
            <section className={styles.node}>
                <SectionTitle data={parseColorString(t("player.node"))} />
                <div className={styles.content}>
                    <div className={styles.detail}>
                        <Count value={data?.node} />
                    </div>
                </div>
            </section>
            <section className={styles.operator}>
                <SectionTitle data={parseColorString(t("player.operator"))} />
                <div className={styles.content}>
                    <div className={classNames(styles.col, styles.hope)}>
                        <div className={styles.detail}>
                            <div className={styles.desc}>
                                {t("player.hope")}
                            </div>
                            <Count value={data?.hope} />
                        </div>
                    </div>
                    <div className={classNames(styles.col, styles.upgrade)}>
                        <div className={styles.detail}>
                            <div className={styles.desc}>
                                {t("player.upgrade")}
                            </div>
                            <Count value={data?.upgrade} />
                        </div>
                    </div>
                </div>
            </section>
            <section className={styles.candle}>
                <SectionTitle data={parseColorString(t("player.candle"))} />
                <div className={classNames(styles.content, styles.center)}>
                    <div className={styles.detail}>
                        <Count value={data?.candle} />
                    </div>
                </div>
            </section>
            {__ROGUE_5_DLC >= 1 ? (
                <section className={styles.expeditionGuide}>
                    <SectionTitle
                        data={parseColorString(t("player.expeditionGuide"))}
                    />
                    <div className={classNames(styles.content, styles.center)}>
                        <div className={styles.detail}>
                            <Count value={data?.expeditionGuide} />
                        </div>
                    </div>
                </section>
            ) : null}
            <section className={styles.multiple}>
                <div className={styles.candleByBattle}>
                    <SectionTitle
                        className={styles.title}
                        data={parseColorString(t("player.candleByBattle"))}
                    />
                    <div className={styles.content}>
                        <Count value={data?.candleByBattle} />
                    </div>
                </div>
                <div className={styles.expedition}>
                    <SectionTitle
                        className={styles.title}
                        data={parseColorString(t("player.expedition"))}
                    />
                    <div className={styles.content}>
                        <Count value={data?.expedition} />
                    </div>
                </div>
            </section>

            <PartTitle
                label={t("player.2.title")}
                subLabel={t("player.2.title.sub")}
            />
            <section className={styles.trade}>
                <SectionTitle data={parseColorString(t("player.trade"))} />
                <div className={classNames(styles.content, styles.center)}>
                    <div className={styles.detail}>
                        <div className={styles.desc}>
                            {t("player.trade.desc")}
                        </div>
                        <Count value={data?.trade} />
                    </div>
                </div>
            </section>
            <section className={styles.skyBattle}>
                <SectionTitle data={parseColorString(t("player.skyBattle"))} />
                <div className={classNames(styles.content, styles.center)}>
                    <div className={styles.detail}>
                        <div className={styles.desc}>
                            {t("player.skyBattle.desc")}
                        </div>
                        <Count value={data?.skyBattle} />
                    </div>
                </div>
            </section>
            <section className={styles.multiple}>
                <div className={styles.killRgdysm}>
                    <SectionTitle
                        className={styles.title}
                        data={parseColorString(t("player.killRgdysm"))}
                    />
                    <div className={styles.content}>
                        <div className={styles.detail}>
                            <div className={styles.desc}>
                                {t("player.killRgdysm.desc")}
                            </div>
                            <Count value={data?.killRgdysm} />
                        </div>
                    </div>
                </div>
                <div className={styles.dyspllKillChar}>
                    <SectionTitle
                        className={styles.title}
                        data={parseColorString(t("player.dyspllKillChar"))}
                    />
                    <div className={styles.content}>
                        <div className={styles.detail}>
                            <div className={styles.desc}>
                                {t("player.dyspllKillChar.desc")}
                            </div>
                            <Count value={data?.dyspllKillChar} />
                        </div>
                    </div>
                </div>
            </section>
            <section className={styles.multiple}>
                <div className={styles.wrath}>
                    <SectionTitle
                        className={styles.title}
                        data={parseColorString(t("player.wrath"))}
                    />
                    <div className={styles.content}>
                        <div className={styles.detail}>
                            <div className={styles.desc}>
                                {t("player.wrath.desc")}
                            </div>
                            <Count value={data?.wrath?.total} />
                        </div>
                    </div>
                </div>
                <div className={styles.wrath12}>
                    <SectionTitle
                        className={styles.title}
                        data={parseColorString(t("player.wrath.12"))}
                    />
                    <div className={styles.content}>
                        <div className={styles.detail}>
                            <div className={styles.desc}>
                                {t("player.wrath.12.desc")}
                            </div>
                            <Count value={data?.wrath?.cnt12} />
                        </div>
                    </div>
                </div>
            </section>

            {maxCopperGain || copperDrawTotal ? (
                <PartTitle
                    label={t("player.3.title")}
                    subLabel={t("player.3.title.sub")}
                />
            ) : null}
            {maxCopperGain ? (
                <section className={styles.copperGain}>
                    <SectionTitle
                        data={parseColorString(t("player.copper.gain"))}
                    />
                    <div className={classNames(styles.content, styles.center)}>
                        <Copper
                            id={maxCopperGain.id}
                            className={styles.copper}
                        />
                        <div className={styles.detail}>
                            <div className={styles.desc}>
                                {maxCopperGain.name}
                            </div>
                            <Count value={maxCopperGain.value} />
                        </div>
                    </div>
                </section>
            ) : null}
            {copperDrawTotal ? (
                <section className={styles.copperDraw}>
                    <SectionTitle
                        data={parseColorString(t("player.copper.draw"))}
                    />
                    <div className={styles.content}>
                        <CopperDraw data={fragmentData} />
                    </div>
                    <div className={styles.total}>
                        <span>{t("player.copper.draw.total.1")}</span>
                        <span>{t("player.copper.draw.total.2")}</span>
                        <span className={styles.highlight}>
                            {copperDrawTotal}
                        </span>
                        <span>{t("player.copper.draw.total.unit")}</span>
                    </div>
                </section>
            ) : null}
        </div>
    )
}

export default PlayerSummary
